import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import OrderedTicket, {
    OrderedTicketItem,
} from 'components/shared/OrderedTicket'
import RouteTitle from 'components/shared/RouteTitle'
import { directionNames } from 'routes/Upsell/Bus/data'
import {
    TransportDepartureType,
    TransportRouteType,
} from 'store/api-slice/types'
import BusTicketEditor from 'routes/selfcare/views/ManageOrder/editors/BusTicketEditor'
import { useSelfcare } from 'store/selfcare-slice/hooks'
import { getDayNameAndDate, getDayNameAndDateWithTime } from 'helpers/dates'
import { useAutoScroll } from 'utils/hooks'
import { RouteDetails } from 'routes/Upsell/Bus/fields/BusDatesAndHours'
import { Color } from 'styles/types'
import {
    TransportProductEditResponseDto,
    TransportReservation,
} from 'store/selfcare-slice/types'
import { isRouteShuttle } from 'routes/Upsell/Bus/helpers'

interface BusTicketProps {
    ticket: TransportReservation
    index: number
}

export type ChangesDataAttr = {
    quantity?: number
    routeType?: TransportRouteType
    departurePlace?: TransportDepartureType
    date?: string
    returnDate?: string
    time?: string
    returnTime?: string
    isRequiresPurchaseTimeSlot?: boolean
}

const BusTicket = ({ ticket, index }: BusTicketProps) => {
    const { formatMessage } = useIntl()
    const { calculateScroll } = useAutoScroll()

    const [isEdited, setIsEdited] = useState(false)

    const { original, changeType, changes, isActive, modificationId } = ticket

    const [temporaryChanges, setTemporaryChanges] = useState(
        changes || original
    )

    const { detail, quantity } = temporaryChanges || {}
    const {
        boxId,
        departurePlace,
        routeType,
        isShuttle,
        date,
        time,
        returnTime,
        returnDate,
        orderTransportTicketId,
    } = detail || {}

    const [routeDetails, setRouteDetails] = useState<RouteDetails>({})
    const [isActionErrors, setIsActionErrors] = useState(false)

    const { deleteItem, revertDeleteItem } = useSelfcare()

    const handleDelete = () => {
        setIsActionErrors(false)
        deleteItem({
            productType: 'bus_ticket',
            detail: { orderTransportTicketId },
        }).then(({ isSuccess }) => {
            if (isSuccess) {
                setIsEdited(false)
                setTemporaryChanges(changes || original)
            } else {
                setIsActionErrors(true)
            }
        })
    }

    const handleRevert = () => {
        if (!modificationId) return

        revertDeleteItem(modificationId)
    }

    const handleCancel = () => {
        setIsEdited(false)
        setTemporaryChanges(changes || original)
        calculateScroll({
            trigerringSection: 'transport',
            trigerringAction: 'cancel_edit',
            targetId: orderTransportTicketId,
        })
    }

    const handleChanges = (data: ChangesDataAttr) => {
        const {
            quantity: changedQuantity,
            routeType: changedRouteType,
            departurePlace: changedDeparturePlace,
            date: changedDate,
            returnDate: changedReturnDate,
            time: changedTime,
            returnTime: changedReturnTime = null,
        } = data

        const isOneDirection = routeType !== 'to_from_park'
        const isTimeCleared =
            changedRouteType ||
            changedDeparturePlace ||
            changedDate ||
            changedReturnDate

        const changes: TransportProductEditResponseDto = {
            ...temporaryChanges,
            quantity: changedQuantity || quantity,
            detail: {
                ...detail,
                isShuttle: isRouteShuttle(changedDeparturePlace) || isShuttle,
                routeType: changedRouteType || routeType,
                departurePlace: changedDeparturePlace || departurePlace,
                date: changedDate || date,
                returnDate: changedRouteType ? returnDate || date : returnDate,
                time: isTimeCleared ? null : changedTime || time,
                returnTime: isTimeCleared
                    ? null
                    : changedReturnTime || returnTime,
            },
        }

        // additional, more complex calculations for changes in some parameters
        if (changedDate) {
            const recalculatedReturnDate =
                returnDate < changedDate ? changedDate : returnDate

            changes.detail.returnDate = isOneDirection
                ? ''
                : recalculatedReturnDate
        } else if (changedReturnDate) {
            changes.detail.date = isOneDirection ? changedReturnDate : date
            changes.detail.returnDate = isOneDirection ? '' : changedReturnDate
        } else if (changedReturnTime) {
            changes.detail.time = isOneDirection ? changedReturnTime : time
            changes.detail.returnTime = changedReturnTime
        }

        setTemporaryChanges(changes)
    }

    const {
        detail: {
            departurePlace: originalDeparturePlace,
            routeType: originalRouteType,
            date: originalDate,
            returnDate: originalReturnDate,
            time: originalTime,
            returnTime: originalReturnTime,
        },
        price: originalPrice,
    } = changes || original

    const from =
        originalRouteType === 'from_park'
            ? directionNames.suntago
            : directionNames[originalDeparturePlace]
    const to =
        originalRouteType === 'from_park'
            ? directionNames[originalDeparturePlace]
            : directionNames.suntago

    const items: OrderedTicketItem[] = []

    const isReturned = changeType === 'return'
    const routeColor: Color = isReturned ? 'grayscale_700' : 'grayscale_900'

    items.push({
        text: (
            <RouteTitle
                from={formatMessage(from)}
                to={formatMessage(to)}
                hasSmallDots
                variant="bodySmall"
                color={routeColor}
            />
        ),
        date: originalTime
            ? getDayNameAndDateWithTime({
                  date: originalDate,
                  time: originalTime,
              })
            : getDayNameAndDate({ date: originalDate }),
    })

    if (originalRouteType === 'to_from_park') {
        items.push({
            text: (
                <RouteTitle
                    from={formatMessage(to)}
                    to={formatMessage(from)}
                    hasSmallDots
                    variant="bodySmall"
                    color={routeColor}
                />
            ),
            date: originalReturnTime
                ? getDayNameAndDateWithTime({
                      date: originalReturnDate,
                      time: originalReturnTime,
                  })
                : getDayNameAndDate({ date: originalReturnDate }),
        })
    }

    const ticketPrice = originalPrice || 0

    useEffect(() => {
        setTemporaryChanges(changes || original)
    }, [modificationId])

    useEffect(() => {
        setTemporaryChanges({
            ...temporaryChanges,
            detail: {
                ...temporaryChanges.detail,
                isRequiresPurchaseTimeSlot:
                    routeDetails.isRequiresPurchaseTimeSlot,
            },
        })
    }, [routeDetails])

    return (
        <OrderedTicket
            id={`transport-ticket-${orderTransportTicketId}`}
            key={boxId}
            items={items}
            price={ticketPrice}
            isEdited={isEdited}
            setIsEdited={setIsEdited}
            isDisabled={!isActive}
            onDelete={handleDelete}
            editedItem={
                changes?.quantity && changes.quantity !== original.quantity
                    ? 'quantity'
                    : undefined
            }
            creator={
                <BusTicketEditor
                    handleCancel={handleCancel}
                    newQuantity={quantity || 1}
                    handleSetQuantity={(quantity) =>
                        handleChanges({ quantity })
                    }
                    currentPrice={ticketPrice}
                    unitPrice={routeDetails.unitPrice}
                    ticket={temporaryChanges}
                    routeDetails={routeDetails}
                    setRouteDetails={setRouteDetails}
                    setChanges={handleChanges}
                    isModified={
                        JSON.stringify(temporaryChanges) !==
                        JSON.stringify(changes || original)
                    }
                    index={index}
                    isRequiresTimeSlotOriginal={
                        original.detail.isRequiresPurchaseTimeSlot
                    }
                />
            }
            isDeleted={isActive && changeType === 'return'}
            onRevert={handleRevert}
            onEdit={() => {
                calculateScroll({
                    trigerringSection: 'transport',
                    trigerringAction: 'edit',
                    targetId: boxId,
                })
            }}
            isActionError={isActionErrors}
            testIdPrefix={`selfcare-transport-${index}`}
        />
    )
}

export default BusTicket
